import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Alert } from '@nula/core-ui';

import { creditState } from './credit-state';
import { formatCreditResetDate } from './format';
import styles from './credit-banner.module.css';

const root = cva(styles.root);

/**
 * SOURCE OF TRUTH: the `recording_minute` row of the server's `credit_prices`
 * table, seeded as `('recording_minute', 8, 1)`. The rate is data, not code:
 * it can be repriced with a new `effective_from` row and no client change.
 * Nothing fetches it — the credits endpoint returns only balance, allowance and
 * reset date — so this copy can silently drift.
 *
 * If the server rate changes, update this constant. Drift affects only the
 * "about N minutes left" estimate in the low-credit banner, never the charge,
 * but a stale rate overstates or understates the user's remaining minutes.
 */
const CREDITS_PER_RECORDING_MINUTE = 8;

export type CreditBannerProps = {
  balance: number;
  monthlyAllowance: number;
  /** RFC3339 instant the allowance refills. */
  creditsResetAt: string;
  className?: string;
};

export function CreditBanner({ balance, creditsResetAt, className }: CreditBannerProps) {
  const state = creditState(balance);

  if (state === 'normal') return null;

  if (state === 'low') {
    const minutesLeft = Math.max(0, Math.floor(balance / CREDITS_PER_RECORDING_MINUTE));
    return (
      <Alert
        type="warning"
        banner
        showIcon
        message="Running low on credits."
        description={`About ${minutesLeft} minutes of meeting notes left this month.`}
        className={clsx(root(), className)}
      />
    );
  }

  // Overdrawn and blocked share the copy: the banner says what happens next,
  // never the negative number — that lives on the meter.
  return (
    <Alert
      type="error"
      banner
      showIcon
      message="You've used all your credits."
      description={`Recording and AI keep working a little longer, then pause until ${formatCreditResetDate(
        creditsResetAt,
      )}.`}
      className={clsx(root(), className)}
    />
  );
}
